import { css } from "@/utils/css";
import { useAutoPostings } from "@/hooks/use-auto-postings";
import { __ } from "@wordpress/i18n";
import { Spinner } from "@wordpress/components";
import { Center } from "@/components/center";
import { WEEKDAYS } from "@/utils/constants";
import { LuCalendar } from "react-icons/lu";
import { Pill } from "@/components/pill";
import { differenceInSeconds, format, formatDate } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { formatDuration, formatTime } from "@/utils/format";
import { DropdownMenu } from "@wordpress/components";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useModal } from "@/hooks/use-modal";
import { toast } from "react-toastify";
import { api } from "@/config/api-fetch";
import { FaAngleLeft, FaAngleRight, FaAnglesLeft, FaAnglesRight } from "react-icons/fa6";
import { isValidDate, phpDate, getNextScheduledRun } from "@/utils/date";
import { Switch } from "@/components/switch";
import { Button } from "@/components/button";
import { FaRegClock } from "react-icons/fa";
import { CronStatus } from "./cron-status";

const weekdays = WEEKDAYS.map((day) => day.shortLabel);

export function Postings() {
    const { data, isLoading } = useAutoPostings();
    const { openModal } = useModal();

    if (!data?.length) {
        return (
            <Center className={css("p-16")}>
                {isLoading ? (
                    <Spinner className={css("w-6 h-6")} />
                ) : (
                    <div className={css("text-silver text-center text-xl italic font-semibold")}>{__("No postings were found", "antimanual")}</div>
                )}
            </Center>
        );
    }

    return (
        <div className={css("p-8")}>
            <CronStatus />
            <div className={css("postings-grid")}>
                {data.map((posting) => {
                    const more = posting.topics.length - 1;
                    const expDate = posting.exp_date;
                    const isExpired = !isValidDate(expDate) || expDate < new Date();
                    const isActive = posting.is_active;

                    return (
                        <div key={posting.post_id} className={css("posting-card flex flex-col justify-between", posting.status)}>
                            <div>
                                <div className={css("posting-header")}>
                                    <div className={css("flex flex-col gap-1")}>
                                        <span className={css("posting-id")}>
                                            {__("ID:", "antimanual")} {posting.post_id}
                                        </span>
                                        <span className={css("status-badge small", posting.status)}>{posting.status}</span>
                                    </div>
                                    <div className={css("flex items-center gap-2")}>
                                        <ToggleStatus postingId={posting.post_id} />
                                        <ActionsMenu postingId={posting.post_id} />
                                    </div>
                                </div>

                                <h3 className={css("leading-lg text-lg font-semibold text-dark")}>
                                    {posting.topics[0] || __("(No Topic)", "antimanual")}
                                </h3>

                                {posting.topics.length > 1 && (
                                    <p
                                        className={css("text-primary cursor-pointer max-w-max")}
                                        onClick={() =>
                                            openModal(
                                                <div className={css("p-6")}>
                                                    <h3>{__("Topics", "antimanual")}</h3>
                                                    <ol>
                                                        {posting.topics.map((topic) => (
                                                            <li key={topic}>{topic}</li>
                                                        ))}
                                                    </ol>
                                                </div>
                                            )
                                        }
                                    >
                                        +{more} {more === 1 ? __("more topic", "antimanual") : __("more topics", "antimanual")}
                                    </p>
                                )}
                            </div>

                            <div>
                                <div className={css("bg-white p-3 rounded-2")}>
                                    <div className={css("schedule-header")}>
                                        <LuCalendar />
                                        <span>{__("Schedule", "antimanual")}</span>
                                    </div>

                                    <div className={css("mb-3")}>
                                        <div className={css("flex flex-wrap gap-1")}>
                                            {weekdays.map((day, idx) => {
                                                const isSelected = posting.weekdays.includes(idx);
                                                return (
                                                    <span key={idx} className={css("weekday-pill", isSelected ? "selected" : "unselected")}>
                                                        {day}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className={css("flex flex-wrap gap-2 mt-4")}>
                                        {/* Times are already converted to local by useAutoPostings hook */}
                                        {posting.times.map((time, i) => (
                                            <Pill key={i} className={css("flex items-center gap-1")}>
                                                <FaRegClock />
                                                <span>{formatTime(time.hour, time.minute)}</span>
                                            </Pill>
                                        ))}
                                    </div>
                                </div>
                                {posting.attachments?.length > 0 && (
                                    <div className={css("mt-3 text-gray font-semibold flex items-center gap-2 flex-wrap")}>
                                        <span className={css("text-uppercase")}>{__("Knowledge Files:", "antimanual")}</span>
                                        <ul className={css("m-0 flex items-center gap-2 flex-wrap")}>
                                            {posting.attachments.map((attachment) => {
                                                return (
                                                    <li className={css("m-0")} key={attachment.id}>
                                                        <a className={css("link")} href={attachment.url} target="_blank">
                                                            {attachment.title}
                                                        </a>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                            </div>

                            <div className={css("mt-4 flex justify-between items-end text-gray font-semibold")}>
                                <div className={css("mt-4 flex flex-col gap-1")}>
                                    <div className={css("flex gap-2 items-center")}>
                                        <span>{__("Type:", "antimanual")}</span> <Pill className={css("post-type")}>{posting.type}</Pill>
                                    </div>
                                    <div className={css("flex gap-2 items-center")}>
                                        <span>{__("Author:", "antimanual")}</span>{" "}
                                        {!posting.author.display_name ? (
                                            __("N/A", "antimanual")
                                        ) : (
                                            <a className={css("link")} href={posting.author.url} target="_blank">
                                                {posting.author.display_name}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className={css("flex flex-col items-start font-semibold text-gray")}>
                                    <span className={css("text-xs")}>{__("Expiration Date", "antimanual")}</span>
                                    <span>{formatDate(expDate, "PPpp")}</span>
                                </div>
                                {isExpired ? (
                                    <span>{__("EXPIRED", "antimanual")}</span>
                                ) : !isActive ? (
                                    <span>{__("PAUSED", "antimanual")}</span>
                                ) : (
                                    <NextRun postingId={posting.post_id} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function ToggleStatus({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);
    const [isActive, setIsActive] = useState<boolean>(posting?.is_active || false);
    const [isToggling, setIsToggling] = useState<boolean>(false);

    if (!posting) return null;

    const expDate = posting.exp_date;
    const isExpired = !isValidDate(expDate) || expDate < new Date();

    const handleToggleStatus = async (is_active: boolean) => {
        setIsToggling(true);

        await toast.promise(
            api
                .put(`/auto-posting?auto_posting_id=${posting.post_id}`, { is_active })
                .then(() =>
                    refetch().finally(() => {
                        setIsActive(is_active);
                    })
                )
                .finally(() => setIsToggling(false)),
            {
                pending: __("Updating posting status...", "antimanual"),
                success: __("Posting status updated successfully!", "antimanual"),
                error: __("Failed to update the posting status.", "antimanual"),
            }
        );
    };

    return <Switch checked={isActive} onChange={handleToggleStatus} disabled={isToggling || isExpired} />;
}

function ActionsMenu({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);
    const { openModal, confirm } = useModal();

    if (!posting) return null;

    const actions = [
        {
            title: __("Details", "antimanual"),
            onClick: () => {
                openModal(<AutoPostingDetails postingId={posting.post_id} />);
            },
        },
        {
            title: __("Generated Posts", "antimanual") + ` (${posting.posts.length || 0})`,
            onClick: () => {
                openModal(<GeneratedPosts postingId={posting.post_id} />);
            },
        },
        {
            title: __("Trigger Now", "antimanual"),
            onClick: () => {
                toast.promise(
                    api.get(`/auto-posting/trigger?auto_posting_id=${posting.post_id}`).then(() => refetch()),
                    {
                        pending: __("Triggering auto posting...", "antimanual"),
                        success: __("Auto posting triggered successfully!", "antimanual"),
                        error: __("Failed to trigger auto posting.", "antimanual"),
                    }
                );
            },
        },
        {
            title: __("Delete", "antimanual"),
            isDanger: true,
            onClick: async () => {
                const confirmed = await confirm(__("Are you sure you want to delete this auto posting?", "antimanual"));
                if (!confirmed) return;

                toast.promise(
                    api.delete(`/auto-posting?auto_posting_id=${posting.post_id}`).then(() => refetch()),
                    {
                        pending: __("Deleting the auto posting...", "antimanual"),
                        success: __("The auto posting was deleted successfully!", "antimanual"),
                        error: __("Failed to delete the auto posting.", "antimanual"),
                    }
                );
            },
        },
    ];

    return (
        <DropdownMenu icon={<BsThreeDotsVertical className={css("w-5 h-5 text-gray")} />} label={__("Select an action", "antimanual")}>
            {(props) => (
                <ul {...props} className={css("dropdown-menu")}>
                    {actions.map((action) => (
                        <li key={action.title} className={css("menu-item", action.isDanger ? "is-danger" : "")} onClick={action.onClick}>
                            {action.title}
                        </li>
                    ))}
                </ul>
            )}
        </DropdownMenu>
    );
}

function NextRun({ postingId }: { postingId: number }) {
    const { data } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);

    const [seconds, setSeconds] = useState<number>(0);

    const calculateNextRun = useMemo(() => {
        return () => {
            if (!posting) return 0;

            // Weekdays and times are already in local timezone (converted by useAutoPostings hook)
            const nextRun = getNextScheduledRun(posting.weekdays, posting.times);

            if (!nextRun || !isValidDate(nextRun)) return 0;

            const now = new Date();
            const secondsUntil = differenceInSeconds(nextRun, now);

            return secondsUntil;
        };
    }, [posting]);

    useEffect(() => {
        // Initial calculation
        setSeconds(calculateNextRun());

        // Update every second for smooth countdown
        const interval = setInterval(() => {
            setSeconds(calculateNextRun());
        }, 500);

        return () => clearInterval(interval);
    }, [calculateNextRun]);

    if (!posting?.is_active) return null;

    if (seconds <= 0) {
        return <div>{__("N/A", "antimanual")}</div>;
    }

    return (
        <div className={css("flex flex-col items-start font-semibold text-gray")}>
            <span className={css("text-xs")}>{__("NEXT RUN", "antimanual")}</span>
            <span className={css("font-mono")}>{formatDuration(seconds)}</span>
        </div>
    );
}

function GeneratedPosts({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const [retrying, setRetrying] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 5;

    const allPosts = useMemo(() => {
        const posting = data?.find((p) => p.post_id === postingId);
        return posting?.posts || [];
    }, [data, postingId]);

    // Paginated posts
    const totalPages = Math.ceil(allPosts.length / postsPerPage);
    const paginatedPosts = useMemo(() => {
        const startIndex = (currentPage - 1) * postsPerPage;
        return allPosts.slice(startIndex, startIndex + postsPerPage);
    }, [allPosts, currentPage, postsPerPage]);

    useEffect(() => {
        refetch();
    }, [postingId]);

    // Reset to page 1 when posts change
    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(1);
        }
    }, [totalPages, currentPage]);

    const handleRetry = async (postId: number) => {
        setRetrying(postId);

        await api.post<{ success: boolean; message?: string }>("/auto-posting/retry-post", { post_id: postId }).catch(() => {})
        await refetch().catch(() => {});

        setRetrying(null);
    };

    if (!allPosts.length) {
        return (
            <Center className={css("w-64 h-40 p-16")}>
                <p className={css("text-silver italic text-lg font-semibold")}>{__("No posts generated yet.", "antimanual")}</p>
            </Center>
        );
    }

    const getGenerationStatusBadge = (post: { auto_post_status?: string; auto_post_error?: string }) => {
        const status = post.auto_post_status || "completed";

        if (status === "pending") {
            return (
                <span className={css("status-badge pending")} title={__("Content is being generated...", "antimanual")}>
                    ⏳ {__("Generating", "antimanual")}
                </span>
            );
        }

        if (status === "failed") {
            return <span className={css("status-badge failed")}>❌ {__("Failed", "antimanual")}</span>;
        }

        return <span className={css("status-badge completed")}>✅ {__("Completed", "antimanual")}</span>;
    };

    return (
        <div className={css("auto-posting")}>
            <div className={css("generated-posts-container")}>
                <div className={css("generated-posts-header")}>
                    <h3 className={css("generated-posts-title")}>{__("Generated Posts", "antimanual")}</h3>
                    {allPosts.length > 0 && (
                        <span className={css("posts-count")}>
                            {allPosts.length} {allPosts.length === 1 ? __("post", "antimanual") : __("posts", "antimanual")}
                        </span>
                    )}
                </div>

                {allPosts.length === 0 ? (
                    <div className={css("no-posts")}>
                        <p>{__("No posts generated yet.", "antimanual")}</p>
                    </div>
                ) : (
                    <>
                        <div className={css("posts-list")}>
                            {paginatedPosts.map((post) => {
                                const created_at = phpDate(post.created_at);
                                const run_date = phpDate(post.run_date || "");
                                const autoPostStatus = post.auto_post_status || "completed";
                                const isFailed = autoPostStatus === "failed";
                                const isPending = autoPostStatus === "pending";
                                const isRetryingThis = retrying === post.id;

                                return (
                                    <div key={post.id} className={css("post-card", autoPostStatus)}>
                                        {/* Status indicator bar */}
                                        <div className={css("status-indicator", autoPostStatus)} />

                                        {/* Main content */}
                                        <div className={css("post-content")}>
                                            {/* Header: Title + Status */}
                                            <div className={css("post-header")}>
                                                <div className={css("post-title-section")}>
                                                    {post.title ? (
                                                        <a href={post.view_link} target="_blank" className={css("post-title-link")}>
                                                            {post.title}
                                                        </a>
                                                    ) : (
                                                        <span className={css("no-title")}>{__("(No Title)", "antimanual")}</span>
                                                    )}
                                                    <Pill className={css("post-type-pill")}>{post.type}</Pill>
                                                </div>
                                                <div className={css("post-status-section")}>{getGenerationStatusBadge(post)}</div>
                                            </div>

                                            {/* Meta info */}
                                            <div className={css("post-meta")}>
                                                <div className={css("meta-item")}>
                                                    <span className={css("meta-label")}>{__("Created:", "antimanual")}</span>
                                                    <span className={css("meta-value")}>
                                                        {isValidDate(created_at)
                                                            ? format(created_at, "MMM d, yyyy 'at' h:mm a")
                                                            : __("N/A", "antimanual")}
                                                    </span>
                                                </div>
                                                <div className={css("meta-item")}>
                                                    <span className={css("meta-label")}>{__("Scheduled:", "antimanual")}</span>
                                                    <span className={css("meta-value")}>
                                                        {isValidDate(run_date)
                                                            ? format(run_date, "MMM d, yyyy 'at' h:mm a")
                                                            : __("N/A", "antimanual")}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Error message - inside the card for failed posts */}
                                            {isFailed && post.auto_post_error && (
                                                <div className={css("error-box")}>
                                                    <div className={css("error-icon")}>⚠️</div>
                                                    <div className={css("error-content")}>
                                                        <strong>{__("Generation Failed", "antimanual")}</strong>
                                                        <p>{post.auto_post_error}</p>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Actions */}
                                            <div className={css("post-actions")}>
                                                {isFailed && (
                                                    <Button small variant="primary" loading={isRetryingThis} onClick={() => handleRetry(post.id)}>
                                                        {__("Retry Generation", "antimanual")}
                                                    </Button>
                                                )}
                                                {!isFailed && !isPending && (
                                                    <>
                                                        <a href={post.edit_link} target="_blank" className={css("action-link")}>
                                                            {__("Edit", "antimanual")}
                                                        </a>
                                                        <a href={post.view_link} target="_blank" className={css("action-link")}>
                                                            {__("View", "antimanual")}
                                                        </a>
                                                    </>
                                                )}
                                                {isPending && (
                                                    <span className={css("pending-text")}>
                                                        <Spinner /> {__("Generating content...", "antimanual")}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className={css("pagination")}>
                                <span className={css("pagination-info")}>
                                    {__("Page", "antimanual")} {currentPage} {__("of", "antimanual")} {totalPages}
                                </span>
                                <div className={css("pagination-buttons")}>
                                    <button className={css("pagination-btn")} onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                        <FaAnglesLeft />
                                    </button>
                                    <button
                                        className={css("pagination-btn")}
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <FaAngleLeft />
                                    </button>
                                    <button
                                        className={css("pagination-btn")}
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <FaAngleRight />
                                    </button>
                                    <button
                                        className={css("pagination-btn")}
                                        onClick={() => setCurrentPage(totalPages)}
                                        disabled={currentPage === totalPages}
                                    >
                                        <FaAnglesRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function AutoPostingDetails({ postingId }: { postingId: number }) {
    const { data, refetch } = useAutoPostings();
    const posting = useMemo(() => data?.find((p) => p.post_id === postingId), [data, postingId]);

    useEffect(() => {
        refetch();
    }, [postingId]);

    if (!posting) return null;

    const expDate = posting.exp_date;
    const isExpired = !isValidDate(expDate) || expDate < new Date();
    const isActive = posting.is_active;

    const preferences = [
        {
            name: __("TONE", "antimanual"),
            value: posting.tone || __("N/A", "antimanual"),
            class: "max-w-lg col-span-2",
        },
        {
            name: __("Article Language", "antimanual"),
            value: posting.language || __("N/A", "antimanual"),
        },
        {
            name: __("Article Length", "antimanual"),
            value:
                posting.min_length && posting.max_length
                    ? posting.min_length + " - " + posting.max_length + " " + __("words", "antimanual")
                    : __("N/A", "antimanual"),
        },
        {
            name: __("Slug Language", "antimanual"),
            value: posting.slug_language || __("N/A", "antimanual"),
        },
        {
            name: __("Slug Max Length", "antimanual"),
            value: posting.slug_max_length
                ? __("up to") + " " + posting.slug_max_length + " " + __("characters", "antimanual")
                : __("N/A", "antimanual"),
        },
        {
            name: __("Post Type", "antimanual"),
            value: <span className={css("text-uppercase text-gray")}>{posting.type || __("N/A", "antimanual")}</span>,
        },
        {
            name: __("Post Status", "antimanual"),
            value: <span className={css("text-uppercase text-gray")}>{posting.status || __("N/A", "antimanual")}</span>,
        },
        {
            name: __("Author", "antimanual"),
            value:
                posting.author.url && posting.author.display_name ? (
                    <a className={css("link")} href={posting.author.url} target="_blank">
                        {posting.author.display_name}
                    </a>
                ) : (
                    __("N/A", "antimanual")
                ),
        },
        {
            name: __("Expiration Date", "antimanual"),
            value: formatDate(expDate, "PPpp"),
        },
    ];

    return (
        <div className={css("p-8")}>
            <div className={css("flex items-center gap-2 text-dark")}>
                <h2 className={css("m-0")}>{__("Auto Posting Details", "antimanual")}</h2>
                <span className={css("text-gray text-xs")}>
                    ({isExpired ? __("EXPIRED", "antimanual") : isActive ? __("ACTIVE", "antimanual") : __("PAUSED", "antimanual")})
                </span>
            </div>
            <div>
                <h3 className={css("mt-8")}>{__("Topics", "antimanual")}</h3>
                <ol>
                    {posting.topics.map((topic, i) => (
                        <li key={i} className={css("max-w-lg")}>
                            {topic}
                        </li>
                    ))}
                </ol>
            </div>
            <div>
                <h3 className={css("mt-8")}>{__("Preferences", "antimanual")}</h3>
                <div className={css("grid grid-cols-2 gap-4")}>
                    {preferences.map((pref) => {
                        return (
                            <div key={pref.name} className={css(pref.class)}>
                                <strong>{pref.name}: </strong> {pref.value}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
